import {useEffect, useState} from 'react'
import Loader from '../../custom/loader/Loader'
import {useTranslation} from 'react-i18next'
import {useAppDispatch} from '../../../redux/hooks'
import {GeneralStatistics, PlanningAcademicYears} from '../../../redux/Slice/generalSlice'
import {ApexOptions} from 'apexcharts'
import ReactApexChart from 'react-apexcharts'
import CustomSelect from 'app/custom/CustomSelect'

interface EvaluationData {
  academic_year_id: number
  academic_year_name: string
  strongly_agree_count: number
  agree_count: number
  strongly_disagree_count: number
  somewhat_agree_count: number
}
const EvaluationDashboard = () => {
  const [loader, setLoader] = useState(true)
  const {t} = useTranslation()
  const dispatch = useAppDispatch()
  const [evaluationData, setEvaluationData] = useState<EvaluationData[]>([])
  const [academicYears, setAcademicYears] = useState<any>([])
  const [selectedAy, setSelectedAy] = useState<number[]>([])

  const fetchData = (ayIds: any[]) => {
    setLoader(true)
    const fd = new FormData()
    ayIds.forEach((id) => fd.append('academic_year_id[]', id))
    dispatch(GeneralStatistics(fd)).then((res: any) => {
      if (res.meta.requestStatus === 'fulfilled') {
        setEvaluationData(res.payload || [])
      }
      setLoader(false)
    })
  }

  useEffect(() => {
    dispatch(PlanningAcademicYears()).then((res) => {
      if (res.meta.requestStatus === 'fulfilled') {
        const years = res.payload
        setAcademicYears(years)
        const sorted = [...years].sort((a: any, b: any) => a.name.localeCompare(b.name))
        const lastFour = sorted.slice(-2)
        const selectedIds = lastFour.map((y: any) => y.id)
        setSelectedAy(selectedIds)
        fetchData(selectedIds)
      }
    })
  }, [])

  const parseBackendData = (data: any[]): EvaluationData[] => {
    return data.map((item) => ({
      ...item,
      strongly_agree_count: Number(item.strongly_agree_count) || 0,
      agree_count: Number(item.agree_count) || 0,
      strongly_disagree_count: Number(item.strongly_disagree_count) || 0,
      somewhat_agree_count: Number(item.somewhat_agree_count) || 0,
    }))
  }

  const processEvaluationData = (data: EvaluationData[]) => {
    return data.map((year) => {
      const total =
        year.strongly_agree_count +
        year.agree_count +
        year.strongly_disagree_count +
        year.somewhat_agree_count

      return {
        ...year,
        strongly_agree_percent: total > 0 ? (year.strongly_agree_count / total) * 100 : 0,
        agree_percent: total > 0 ? (year.agree_count / total) * 100 : 0,
        strongly_disagree_percent: total > 0 ? (year.strongly_disagree_count / total) * 100 : 0,
        somewhat_agree_percent: total > 0 ? (year.somewhat_agree_count / total) * 100 : 0,
        total_responses: total,
      }
    })
  }

  const processedData = processEvaluationData(parseBackendData(evaluationData))

  const evaluationSeries = [
    {
      name: 'Excellent',
      data: processedData.map((y) => y.strongly_agree_percent),
    },
    {
      name: 'Très bon/ Very Good',
      data: processedData.map((y) => y.agree_percent),
    },
    {
      name: 'Pas Satisfaisant/ Not Satisfactory',
      data: processedData.map((y) => y.strongly_disagree_percent),
    },
    {
      name: 'Pas de tout satisfaisant/ Not at all Satisfactory',
      data: processedData.map((y) => y.somewhat_agree_percent),
    },
  ]

  // Chart options
  const evaluationOptions: ApexOptions = {
    chart: {
      type: 'bar',
      animations: {enabled: true, speed: 400},
    },
    colors: ['#1E88E5', '#43A047', '#FB8C00', '#E53935'],
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: '40%',
        borderRadius: 6,
        dataLabels: {
          position: 'top',
          hideOverflowingLabels: false,
        },
      },
    },
    dataLabels: {
      enabled: true,
      offsetY: -15,
      style: {
        colors: ['#000'],
        fontSize: '12px',
        fontWeight: 700,
      },
      formatter: (val) => {
        return Number(val) > 0 ? `${Math.round(Number(val))}%` : ''
      },
    },
    xaxis: {
      categories: processedData.map((y) => y.academic_year_name),
      labels: {
        style: {
          fontSize: '12px',
          fontWeight: 'bold',
        },
      },
    },
    yaxis: {
      min: 0,
      max: 100,
      labels: {
        formatter: (val: number) => `${Math.round(Number(val))}%`,
      },
    },
    tooltip: {
      shared: false,
      y: {
        formatter: (val: number, {seriesIndex, dataPointIndex}) => {
          const year = processedData[dataPointIndex]
          const counts = [
            year.strongly_agree_count,
            year.agree_count,
            year.strongly_disagree_count,
            year.somewhat_agree_count,
          ]
          return `${val.toFixed(1)}% (${counts[seriesIndex]} of ${year.total_responses})`
        },
      },
    },
    legend: {
      position: 'bottom',
      horizontalAlign: 'center',
      markers: {
        radius: 12,
        width: 12,
        height: 12,
      },
      itemMargin: {
        horizontal: 10,
        vertical: 5,
      },
    },
    responsive: [
      {
        breakpoint: 768,
        options: {
          chart: {
            height: 450,
          },
          plotOptions: {
            bar: {
              columnWidth: '45%',
            },
          },
          dataLabels: {
            style: {
              fontSize: '9px',
            },
          },
        },
      },
    ],
  }

  if (loader) return <Loader />

  return (
    <div className='card'>
      <div className='card-body'>
        <div className='row'>
          <div className='col-md-10'>
            <label className='form-label fw-bold mb-1 required'>{t('LABEL.ACADEMIC_YEAR')}</label>

            <CustomSelect
              className='input'
              value={academicYears.filter((y) => selectedAy.includes(y.id))}
              onChange={(items: any) => setSelectedAy(items ? items.map((i: any) => i.id) : [])}
              options={academicYears}
              isMulti
            />
          </div>
          <div className='col-md-2 pt-7'>
            <button
              className='btn btn-outline-primary btn-sm fw-bold px-4 w-100'
              onClick={() => selectedAy.length > 0 && fetchData(selectedAy)}
            >
              <span className='fa fa-search mx-2'></span>
              {t('GLOBAL.SEARCH')}
            </button>
          </div>
        </div>
        <h5 className='text-center mb-3 mt-3'>{t('LABEL.COURSE_EVALUATION_SUMMARY')}</h5>
        {evaluationData.length > 0 ? (
          <>
            <ReactApexChart
              options={evaluationOptions}
              series={evaluationSeries}
              type='bar'
              height={350}
              width={'100%'}
            />
          </>
        ) : (
          <div className='text-center py-5'>{t('GLOBAL.DATA_NOT_AVAILABLE')}</div>
        )}
      </div>
    </div>
  )
}

export default EvaluationDashboard
